<template>
  <div class="player-idle-root">
    <transition name="idle-phase" mode="out-in">
      <component :is="phaseComponent" :key="phase" />
    </transition>
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, toRefs, unref, watch } from 'vue'
import PlayerIdleHibernationView from '@/components/PlayerIdleHibernationView.vue'
import PlayerIdleLoadingView from '@/components/PlayerIdleLoadingView.vue'
import PlayerIdleUnregisteredView from '@/components/PlayerIdleUnregisteredView.vue'
import PlayerIdleWaitingView from '@/components/PlayerIdleWaitingView.vue'
import usePlayer from '@/composables/usePlayer'
import { usePlayerIdleHibernationSignals } from '@/composables/usePlayerIdleHibernationSignals'
import { MEDIA_LOAD_CHROME_DELAY_MS, REGISTRATION_IDLE_SETTLE_MS } from '@/constants'
import { shouldShowUnregisteredIdle } from '@/helpers/shouldShowUnregisteredIdle'

const { state } = usePlayer()
const {
  tenant,
  registrationCode,
  pairingAfterUnregisterPending,
  networkStatus,
  queue,
  postPlaylistLoadHoldActive,
  loadingCampaign,
  downloadProgress
} = toRefs(state)

const {
  showHibernationScreen,
  showLastCampaignEndedScreen
} = usePlayerIdleHibernationSignals()

const registrationReceived = computed(
  () =>
    unref(registrationCode) !== null
    || unref(tenant) !== null
    || unref(pairingAfterUnregisterPending)
)

const showDownloadProgress = computed(
  () => unref(loadingCampaign) && !Number.isNaN(unref(downloadProgress))
)

/**
 * True after {@link MEDIA_LOAD_CHROME_DELAY_MS} while download is active, or immediately when
 * the post-playlist hold is on. Fast cache loads stay on waiting / never flash a separate phase.
 */
const showDelayedDownloadChrome = ref(false)
let downloadChromeDelayTimer: ReturnType<typeof setTimeout> | null = null

watch(
  [showDownloadProgress, postPlaylistLoadHoldActive],
  ([show, hold]) => {
    if (downloadChromeDelayTimer !== null) {
      clearTimeout(downloadChromeDelayTimer)
      downloadChromeDelayTimer = null
    }
    if (hold) {
      showDelayedDownloadChrome.value = true
      return
    }
    if (!show) {
      showDelayedDownloadChrome.value = false
      return
    }
    const skipDelay
      = typeof window !== 'undefined'
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (skipDelay) {
      showDelayedDownloadChrome.value = true
      return
    }
    showDelayedDownloadChrome.value = false
    downloadChromeDelayTimer = setTimeout(() => {
      showDelayedDownloadChrome.value = true
      downloadChromeDelayTimer = null
    }, MEDIA_LOAD_CHROME_DELAY_MS)
  },
  { immediate: true }
)

type IdlePhase = 'unregistered' | 'waiting' | 'loading' | 'hibernation'

/** True briefly after registration code clears — keeps “waiting” instead of snapping to download UI. */
const registrationIdleSettling = ref(false)
let registrationSettleClearTimer: ReturnType<typeof setTimeout> | null = null

watch(
  registrationCode,
  (code, prevCode) => {
    if (typeof code === 'string') {
      if (registrationSettleClearTimer !== null) {
        clearTimeout(registrationSettleClearTimer)
        registrationSettleClearTimer = null
      }
      registrationIdleSettling.value = false
      return
    }
    if (typeof prevCode === 'string' && code === null) {
      const skipSettle
        = typeof window !== 'undefined'
          && window.matchMedia('(prefers-reduced-motion: reduce)').matches
      if (skipSettle) {
        return
      }
      if (registrationSettleClearTimer !== null) {
        clearTimeout(registrationSettleClearTimer)
        registrationSettleClearTimer = null
      }
      registrationIdleSettling.value = true
      registrationSettleClearTimer = setTimeout(() => {
        registrationIdleSettling.value = false
        registrationSettleClearTimer = null
      }, REGISTRATION_IDLE_SETTLE_MS)
    }
  }
)

onBeforeUnmount(() => {
  if (registrationSettleClearTimer !== null) {
    clearTimeout(registrationSettleClearTimer)
    registrationSettleClearTimer = null
  }
  if (downloadChromeDelayTimer !== null) {
    clearTimeout(downloadChromeDelayTimer)
    downloadChromeDelayTimer = null
  }
})

const phaseRaw = computed<IdlePhase>(() => {
  if (!unref(networkStatus).connected) {
    return 'waiting'
  }
  if (!unref(registrationReceived)) {
    return 'waiting'
  }
  if (
    shouldShowUnregisteredIdle({
      tenant: unref(tenant),
      registrationCode: unref(registrationCode),
      pairingAfterUnregisterPending: unref(pairingAfterUnregisterPending)
    })
  ) {
    return 'unregistered'
  }

  if (unref(tenant) !== null && unref(postPlaylistLoadHoldActive)) {
    return 'loading'
  }

  const tenantOk = unref(tenant) !== null
  const queueEmpty = unref(queue).length < 1
  /** One continuous loading scene for preparing + download (no cube↔progress phase swap). */
  const loadingMedia
    = tenantOk
      && queueEmpty
      && unref(showDownloadProgress)
      && unref(showDelayedDownloadChrome)

  if (loadingMedia) {
    return 'loading'
  }

  if (tenantOk && queueEmpty && (unref(showHibernationScreen) || unref(showLastCampaignEndedScreen))) {
    return 'hibernation'
  }

  return 'waiting'
})

const phase = computed<IdlePhase>(() => {
  const raw = phaseRaw.value
  if (
    registrationIdleSettling.value
    && raw === 'loading'
    && !unref(postPlaylistLoadHoldActive)
  ) {
    return 'waiting'
  }
  return raw
})

const phaseComponent = computed(() => {
  switch (unref(phase)) {
    case 'unregistered':
      return PlayerIdleUnregisteredView
    case 'loading':
      return PlayerIdleLoadingView
    case 'hibernation':
      return PlayerIdleHibernationView
    default:
      return PlayerIdleWaitingView
  }
})
</script>

<style scoped>
.player-idle-root {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
</style>

<style>
.player-idle-phase {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}
.idle-phase-leave-active {
  transition: opacity 0.48s cubic-bezier(0.4, 0, 1, 1);
  z-index: 1;
}
.idle-phase-enter-active {
  transition: opacity 0.8s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2;
}
.idle-phase-enter-from,
.idle-phase-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .idle-phase-leave-active,
  .idle-phase-enter-active {
    transition-duration: 0.01ms;
  }
}
</style>
